import {
  // Auth Slice
  authenticate,
  getEmailOtp,
  getOtp,

  // Auth API
  useResendOTPMutation,
  useSendOTPMutation,
  useVerifyOTPMutation,

  // Store
  dispatch,
  useAppSelector,

  // Workspace
  onAuthSuccess,
  AuthChannel,
  updateUserRoles,
  OnGuestUserAuthenticated,
} from '@zonofi/zono-money-store';
import { SellerDetails } from '@zonofi/zo-types';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';
import { isEmpty } from 'lodash';
import { CountryObject } from '@zonofi/i18n';
import { isPhoneNumberValid } from '@zonofi/common';

export const useSignUpNow = ({
  onUpdateValue,
  sellerDetails,
}: {
  onOtpVerified: () => void;
  onUpdateValue: (key: string, value: string | boolean) => void;
  sellerDetails: Partial<SellerDetails>;
}) => {
  const navigate = useNavigate();

  const otpFromStore = useAppSelector(getOtp);
  const emailOtpFromStore = useAppSelector(getEmailOtp);
  const location = useLocation();
  const mobileInputRef = useRef<HTMLDivElement | null>(null);
  const [isLoginScreen, setIsLoginScreen] = useState<boolean>(
    location.pathname === '/login'
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loginVal, setLoginVal] = useState<string>();
  const [otpToLogin, setOtpToLogin] = useState<string>('');
  const [showOtpInput, setShowOtpInput] = useState<boolean>(false);
  const [inputErrors, setInputErrors] = useState<{
    isError: boolean;
    errorMsg: string;
  }>({
    isError: false,
    errorMsg: '',
  });
  const [selectedCountry, setSelectedCountry] = useState<CountryObject>({
    name: 'India',
    flag: '🇮🇳',
    code: 'IN',
    dial_code: '+91',
    currency_code: 'INR',
    time_zones: ['Asia/Kolkata'],
  });
  const [countryDrawerOpen, setCountryDrawerOpen] = useState<boolean>(false);

  const [sendOtpAPI, sendOtpAPIResponse] = useSendOTPMutation();
  const [verifyOtpAPI, verifyOtpAPIResponse] = useVerifyOTPMutation();
  const [resendOtp, resendOTPResponse] = useResendOTPMutation();

  const [timer, setTimer] = useState<number>(120);
  const [resendEnabled, setResendEnabled] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds
    ).padStart(2, '0')}`;
  };

  const handleInputChange = (e: { target: { value: string } }) => {
    const inputValue = e.target.value;

    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      setLoginVal(inputValue);
    } else {
      // For non-login screens, only allow numeric input
      if (inputValue.length > 0 && !/^\d+$/.test(inputValue)) {
        setInputErrors({
          isError: true,
          errorMsg: 'Only numbers allowed',
        });
        return;
      }

      const value = inputValue.replace(/\D/g, '');
      setLoginVal(value);

      // Clear or validate error message
      if (value.length === 0) {
        setInputErrors({ isError: false, errorMsg: '' });
      } else if (inputErrors?.isError) {
        validateInput(value);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Only restrict input on non-login screens
    if (!isLoginScreen) {
      // Allow navigation keys, backspace, delete, etc.
      if (
        e.key === 'Backspace' ||
        e.key === 'Delete' ||
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        return;
      }

      // Only allow numeric input
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        setInputErrors({
          isError: true,
          errorMsg: 'Only numbers allowed',
        });
      }
    }
  };
  const validateInput = (val: string): boolean => {
    const isValidEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };

    // Only allow email validation on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      const isValid = isValidEmail(val);
      setInputErrors({
        isError: !isValid,
        errorMsg: isValid ? '' : 'Invalid Email Id',
      });
      return isValid;
    } else {
      // For non-login screens, only allow mobile numbers
      const isNumeric = /^\d+$/.test(val);
      const isValid =
        isNumeric && isPhoneNumberValid(val, selectedCountry?.code);

      setInputErrors({
        isError: !isValid,
        errorMsg: !isNumeric
          ? 'Only numbers allowed'
          : !isValid
            ? 'Invalid Mobile Number'
            : '',
      });
      return isValid;
    }
  };

  const onGenerateOtp = useCallback(() => {
    const isValid = validateInput(loginVal || '');
    let payload = {};

    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      payload = {
        authChannel: 'email' as AuthChannel,
        email: loginVal,
        doCreateUser: false,
      };
    } else {
      // For non-login screens, always use mobile
      payload = {
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry?.dial_code}${loginVal}`,
        doCreateUser: false,
      };
    }
    if (isValid) {
      sendOtpAPI({ ...payload });
    }
  }, [loginVal, sellerDetails.isEmail, isLoginScreen, selectedCountry]);

  const onVerifyOtp = useCallback(() => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      setIsLoading(true);
      verifyOtpAPI({
        authChannel: 'email' as AuthChannel,
        email: loginVal,
        otp: otpToLogin,
        mfaStatus: false,
      });
    } else {
      setIsLoading(true);
      // For non-login screens, always use mobile
      verifyOtpAPI({
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry?.dial_code}${loginVal}`,
        otp: otpToLogin,
        mfaStatus: false,
      });
    }
  }, [loginVal, otpToLogin, sellerDetails.isEmail, isLoginScreen]);

  const resendOTPInput = () => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      resendOtp({ authChannel: 'email' as AuthChannel, email: loginVal });
    } else {
      // For non-login screens, always use mobile
      resendOtp({
        authChannel: 'mobile' as AuthChannel,
        mobile: `${selectedCountry}${loginVal}`,
      });
    }
    setOtpToLogin('');
    setErrorMessage('');
    setTimer(120);
  };

  const openCountriesDrawer = () => {
    setCountryDrawerOpen(true);
  };

  const closeCountriesDrawer = () => {
    setCountryDrawerOpen(false);
  };

  useEffect(() => {
    // Only allow email detection on login screen
    if (isLoginScreen) {
      onUpdateValue('isEmail', isNaN(Number(loginVal?.[0])));
    } else {
      // For non-login screens, always set isEmail to false
      onUpdateValue('isEmail', false);
    }
  }, [loginVal, isLoginScreen]);

  useEffect(() => {
    if (sendOtpAPIResponse.isSuccess) {
      if (sellerDetails.isEmail) {
        if (sendOtpAPIResponse?.data?.email?.resendAttemptsLeft > 0) {
          setShowOtpInput(true);
          setTimer(120);
          setResendEnabled(false);
          dispatch(
            OnGuestUserAuthenticated(sendOtpAPIResponse?.data?.temptoken)
          );
        } else if (sendOtpAPIResponse?.data?.email?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.email?.message);
        } else if (sendOtpAPIResponse?.data?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.message);
        }
      } else if (!sellerDetails.isEmail) {
        if (sendOtpAPIResponse?.data?.mobile?.resendAttemptsLeft > 0) {
          setShowOtpInput(true);
          setTimer(120);
          setResendEnabled(false);
          dispatch(
            OnGuestUserAuthenticated(sendOtpAPIResponse?.data?.temptoken)
          );
        } else if (sendOtpAPIResponse?.data?.mobile?.message) {
          setErrorMessage(sendOtpAPIResponse?.data?.mobile?.message);
        } else if (sendOtpAPIResponse?.data?.message) {
          setInputErrors({
            isError: true,
            errorMsg: sendOtpAPIResponse?.data?.message,
          });
        }
      }
    }
  }, [sendOtpAPIResponse]);

  useEffect(() => {
    if (verifyOtpAPIResponse?.isSuccess) {
      setIsLoading(false);
      if (
        verifyOtpAPIResponse.data.attemptsLeft === 0 ||
        verifyOtpAPIResponse?.data?.alertMessage ||
        verifyOtpAPIResponse.data?.message
      ) {
        setErrorMessage(
          verifyOtpAPIResponse?.data?.alertMessage ||
            verifyOtpAPIResponse.data?.message ||
            ''
        );
      } else {
        // Encrypt sensitive data before redirect
        if (sellerDetails.isEmail) {
          onUpdateValue('emailId', loginVal || '');
        }

        // Update user roles
        !isEmpty(verifyOtpAPIResponse?.data?.workspaceRoles) &&
          dispatch(
            updateUserRoles(verifyOtpAPIResponse?.data?.workspaceRoles ?? [])
          );

        const dataToTransfer = verifyOtpAPIResponse?.data;
        dispatch(onAuthSuccess(dataToTransfer));

        // Simple base64 encoding (use proper encryption in production)
        const encodedData = btoa(JSON.stringify(dataToTransfer));

        // Store in localStorage for access in different flows
        localStorage.setItem('authData', encodedData);
        let baseUrl = getConfigValue('APP_ENDPOINT');
        if (baseUrl.endsWith('/')) {
          baseUrl = baseUrl.slice(0, -1);
        }
        // window.location.href = `${baseUrl}/authenticate?data=${encodedData}`;
        dispatch(authenticate());
      }
    }
  }, [verifyOtpAPIResponse]);

  useEffect(() => {
    // Only allow email on login screen
    if (sellerDetails.isEmail && isLoginScreen) {
      setOtpToLogin(emailOtpFromStore);
    } else {
      // For non-login screens, always use mobile
      setOtpToLogin(otpFromStore);
    }
  }, [otpFromStore, emailOtpFromStore, isLoginScreen]);

  useEffect(() => {
    if (showOtpInput) {
      setErrorMessage('');
    }
  }, [showOtpInput]);

  useEffect(() => {
    if (!showOtpInput) {
      return () => {};
    }
    let interval: NodeJS.Timeout | null = null;
    if (timer > 0 && !resendEnabled) {
      //@ts-ignore
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setResendEnabled(true);
      setOtpToLogin('');
      if (interval) {
        clearInterval(interval);
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer, resendEnabled, showOtpInput]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        if (showOtpInput) {
          onVerifyOtp();
        } else {
          onGenerateOtp();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onGenerateOtp, onVerifyOtp, showOtpInput]);

  useEffect(() => {
    if (location.pathname === '/login') {
      setIsLoginScreen(true);
    }
  }, [location]);

  useEffect(() => {
    if (resendOTPResponse.isSuccess && resendOTPResponse.data) {
      if (resendOTPResponse?.data?.sendOtp) {
        setOtpToLogin(resendOTPResponse?.data?.otp?.toString());
        setResendEnabled(false);
      } else {
        const resendAttemptsLeft = resendOTPResponse?.data?.resendAttemptsLeft;
        const message = resendOTPResponse?.data?.message;
        const timeLeft = resendOTPResponse?.data?.timeLeft;
        const uom = resendOTPResponse?.data?.uom;
        if (message) {
          if (resendAttemptsLeft === 0 && timeLeft) {
            setErrorMessage(message);
            setOtpToLogin('');
            setResendEnabled(false);
            setTimer(timeLeft);
            // setTimeLeft({ time: timeLeft, uom: uom });
          }
        }
      }
    } else if (resendOTPResponse.isError) {
      //@ts-ignore
      if (resendOTPResponse?.error?.status === 404) {
        // Only allow email on login screen
        if (sellerDetails.isEmail && isLoginScreen) {
          sendOtpAPI({ authChannel: 'email' as AuthChannel, email: loginVal });
        } else {
          // For non-login screens, always use mobile
          sendOtpAPI({
            authChannel: 'mobile' as AuthChannel,
            mobile: `${selectedCountry?.dial_code}${loginVal}`,
          });
        }
      } else {
        //@ts-ignore
        setErrorMessage(resendOTPResponse?.error?.data?.message);
      }
    }
    // handleOTPResponse(resendOTPResponse);
  }, [resendOTPResponse]);

  useEffect(() => {
    if (sellerDetails.phoneNumber || (sellerDetails.emailId && isLoginScreen)) {
      setShowOtpInput(true);
    }
  }, [
    sellerDetails.isEmail,
    sellerDetails.phoneNumber,
    sellerDetails.emailId,
    isLoginScreen,
  ]);

  useEffect(() => {
    if (sellerDetails.phoneNumber) {
      setShowOtpInput(true);
      setLoginVal(sellerDetails.phoneNumber.replace('091', ''));
      onUpdateValue?.('isEmail', false);
    } else if (sellerDetails?.emailId && isLoginScreen) {
      // Only allow email on login screen
      setShowOtpInput(true);
      setLoginVal(sellerDetails.emailId);
      onUpdateValue?.('isEmail', true);
    }
  }, [isLoginScreen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const anchorEl = mobileInputRef.current;
      const drawerEl = document.querySelector('.countries-list'); // Adjust selector if needed

      if (
        countryDrawerOpen &&
        anchorEl &&
        !anchorEl.contains(event.target as Node) &&
        drawerEl &&
        !drawerEl.contains(event.target as Node)
      ) {
        closeCountriesDrawer();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [countryDrawerOpen]);

  return {
    loginVal,
    setLoginVal,
    otpToLogin,
    setOtpToLogin,
    isEmail: sellerDetails.isEmail,
    onGenerateOtp,
    sendOtpAPIResponse,
    showOtpInput,
    setShowOtpInput,
    onVerifyOtp,
    verifyOtpAPIResponse,
    timer,
    formatTime,
    resendEnabled,
    errorMessage,
    resendOTPInput,
    setErrorMessage,
    inputErrors,
    validateInput,
    setInputErrors,
    isLoginScreen,
    setIsLoginScreen,
    handleInputChange,
    handleKeyDown,
    isLoading,
    selectedCountry,
    setSelectedCountry,
    countryDrawerOpen,
    closeCountriesDrawer,
    openCountriesDrawer,
    mobileInputRef,
  };
};
